import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import {
  Trash2, RotateCcw, CalendarDays, Store, UtensilsCrossed, Tag,
  AlertTriangle, Clock, XCircle, Settings
} from "lucide-react";

const TYPE_CONFIG = {
  events: { label: "Events", icon: CalendarDays, color: "text-indigo-500", bg: "bg-indigo-500/10", idField: "event_id", nameField: "title", type: "event" },
  vendors: { label: "Vendors", icon: Store, color: "text-emerald-500", bg: "bg-emerald-500/10", idField: "vendor_id", nameField: "name", type: "vendor" },
  meals: { label: "Meals", icon: UtensilsCrossed, color: "text-amber-500", bg: "bg-amber-500/10", idField: "meal_id", nameField: "meal_type", type: "meal" },
  categories: { label: "Categories", icon: Tag, color: "text-violet-500", bg: "bg-violet-500/10", idField: "name", nameField: "name", type: "category" },
};

export default function TrashPage() {
  const { authAxios, isAdmin } = useAuth();
  const [trash, setTrash] = useState({ events: [], vendors: [], meals: [], categories: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [purgeAllConfirm, setPurgeAllConfirm] = useState(false);
  const [purgeConfirm, setPurgeConfirm] = useState(null);
  const [retentionDays, setRetentionDays] = useState(30);
  const [retentionInput, setRetentionInput] = useState("30");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);

  const fetchTrash = useCallback(async () => {
    try {
      const api = authAxios();
      const res = await api.get("/trash");
      setTrash(res.data);
    } catch {
      toast.error("Failed to load trash");
    } finally {
      setLoading(false);
    }
  }, [authAxios]);

  const fetchSettings = useCallback(async () => {
    try {
      const api = authAxios();
      const res = await api.get("/settings");
      const days = res.data.trash_retention_days || 30;
      setRetentionDays(days);
      setRetentionInput(String(days));
    } catch {
      // Non-admin users may not have access
    }
  }, [authAxios]);

  useEffect(() => { fetchTrash(); fetchSettings(); }, [fetchTrash, fetchSettings]);

  const handleSaveRetention = async () => {
    const days = parseInt(retentionInput);
    if (isNaN(days) || days < 1 || days > 365) {
      toast.error("Retention must be between 1 and 365 days");
      return;
    }
    setSavingSettings(true);
    try {
      const api = authAxios();
      await api.put("/settings", { trash_retention_days: days });
      setRetentionDays(days);
      setSettingsOpen(false);
      toast.success(`Auto-purge set to ${days} days`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save settings");
    } finally {
      setSavingSettings(false);
    }
  };

  const handleRestore = async (itemType, itemId, itemName) => {
    try {
      const api = authAxios();
      await api.post(`/trash/restore/${itemType}/${encodeURIComponent(itemId)}`);
      toast.success(`"${itemName}" restored successfully`);
      fetchTrash();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to restore item");
    }
  };

  const handlePurge = async (itemType, itemId) => {
    try {
      const api = authAxios();
      await api.delete(`/trash/purge/${itemType}/${encodeURIComponent(itemId)}`);
      toast.success("Item permanently deleted");
      setPurgeConfirm(null);
      fetchTrash();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete item");
    }
  };

  const handlePurgeAll = async () => {
    try {
      const api = authAxios();
      const res = await api.delete("/trash/purge-all");
      toast.success(res.data.message);
      setPurgeAllConfirm(false);
      fetchTrash();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to empty trash");
    }
  };

  const getDaysUntilPurge = (deletedAt) => {
    if (!deletedAt) return retentionDays;
    const deleted = new Date(deletedAt);
    const purgeDate = new Date(deleted.getTime() + retentionDays * 24 * 60 * 60 * 1000);
    const now = new Date();
    const days = Math.ceil((purgeDate - now) / (24 * 60 * 60 * 1000));
    return Math.max(0, days);
  };

  const renderItems = (items, config) => {
    if (items.length === 0) {
      return (
        <div className="text-center py-12">
          <config.icon className="h-12 w-12 mx-auto text-muted-foreground/20 mb-3" />
          <p className="text-muted-foreground">No {config.label.toLowerCase()} in trash</p>
        </div>
      );
    }

    return (
      <div className="space-y-3" data-testid={`trash-${config.type}-list`}>
        {items.map((item) => {
          const id = item[config.idField];
          const name = config.type === "meal"
            ? `${item.meal_type} (Day ${item.day_number})`
            : item[config.nameField] || "Unknown";
          const daysLeft = getDaysUntilPurge(item.deleted_at);

          return (
            <div
              key={id}
              className="flex items-center justify-between p-4 rounded-lg border border-border/50 bg-card hover:bg-muted/50 transition-colors"
              data-testid={`trash-item-${config.type}-${id}`}
            >
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <div className={`p-2 rounded-lg ${config.bg}`}>
                  <config.icon className={`h-4 w-4 ${config.color}`} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-foreground truncate">{name}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    {config.type === "event" && item.status && (
                      <Badge className={`text-[10px] ${
                        item.status === "approved" ? "bg-emerald-500/10 text-emerald-600" :
                        item.status === "pending" ? "bg-amber-500/10 text-amber-600" :
                        item.status === "complete" ? "bg-blue-500/10 text-blue-600" :
                        "bg-red-500/10 text-red-600"
                      }`}>{item.status}</Badge>
                    )}
                    {config.type === "event" && item.start_date && (
                      <span className="text-xs text-muted-foreground">{item.start_date} - {item.end_date}</span>
                    )}
                    {config.type === "meal" && item.event_id && (
                      <span className="text-xs text-muted-foreground">Event: {item.event_id}</span>
                    )}
                    {config.type === "vendor" && item.categories?.length > 0 && (
                      <span className="text-xs text-muted-foreground">{item.categories.join(", ")}</span>
                    )}
                    {item.deleted_at && (
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        Deleted: {new Date(item.deleted_at).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>
                <Badge variant="outline" className={`shrink-0 text-[10px] ${daysLeft <= 7 ? "text-red-500 border-red-500/30" : "text-muted-foreground"}`}>
                  {daysLeft}d until purge
                </Badge>
              </div>
              <div className="flex items-center gap-2 ml-4 shrink-0">
                <Button
                  variant="outline" size="sm"
                  onClick={() => handleRestore(config.type, id, name)}
                  data-testid={`restore-${config.type}-${id}`}
                >
                  <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
                  Restore
                </Button>
                <Button
                  variant="ghost" size="sm"
                  className="text-destructive hover:text-destructive"
                  onClick={() => setPurgeConfirm({ type: config.type, id, name })}
                  data-testid={`purge-${config.type}-${id}`}
                >
                  <XCircle className="h-3.5 w-3.5 mr-1.5" />
                  Delete
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="p-6 md:p-8 lg:p-12 w-full space-y-8" data-testid="trash-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-foreground flex items-center gap-3">
            <Trash2 className="h-8 w-8 text-muted-foreground" />
            Trash
          </h1>
          <p className="text-muted-foreground mt-1">
            {trash.total} item(s) in trash. Items are auto-purged after {retentionDays} days.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <Button variant="outline" size="sm" onClick={() => { setRetentionInput(String(retentionDays)); setSettingsOpen(true); }} data-testid="trash-settings-btn">
              <Settings className="h-4 w-4 mr-2" /> Settings
            </Button>
          )}
          {trash.total > 0 && (
            <Button
              variant="destructive" size="sm"
              onClick={() => setPurgeAllConfirm(true)}
              data-testid="empty-trash-btn"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Empty Trash
            </Button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => <div key={i} className="h-20 bg-muted rounded-xl animate-pulse" />)}
        </div>
      ) : trash.total === 0 ? (
        <Card className="border-border/50 shadow-sm">
          <CardContent className="py-16 text-center">
            <Trash2 className="h-16 w-16 mx-auto text-muted-foreground/20 mb-4" />
            <h3 className="text-lg font-heading font-semibold text-foreground">Trash is empty</h3>
            <p className="text-muted-foreground mt-1">Deleted items will appear here for 30 days before being permanently removed.</p>
          </CardContent>
        </Card>
      ) : (
        <Tabs defaultValue="events" className="w-full" data-testid="trash-tabs">
          <TabsList data-testid="trash-tabs-list">
            {Object.entries(TYPE_CONFIG).map(([key, cfg]) => (
              <TabsTrigger key={key} value={key} data-testid={`trash-tab-${key}`}>
                <cfg.icon className="h-4 w-4 mr-1.5" />
                {cfg.label}
                {trash[key]?.length > 0 && (
                  <Badge variant="secondary" className="ml-1.5 text-[10px] px-1.5">{trash[key].length}</Badge>
                )}
              </TabsTrigger>
            ))}
          </TabsList>

          {Object.entries(TYPE_CONFIG).map(([key, cfg]) => (
            <TabsContent key={key} value={key} className="mt-4">
              {renderItems(trash[key] || [], cfg)}
            </TabsContent>
          ))}
        </Tabs>
      )}

      {/* Purge All Confirmation Dialog */}
      <Dialog open={purgeAllConfirm} onOpenChange={setPurgeAllConfirm}>
        <DialogContent data-testid="purge-all-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Empty Trash
            </DialogTitle>
          </DialogHeader>
          <p className="text-muted-foreground">
            This will <strong>permanently delete</strong> all {trash.total} item(s) in the trash.
            This includes all trashed events, vendors, meals, and categories along with their associated data.
          </p>
          <p className="text-sm text-destructive font-medium">This action cannot be undone.</p>
          <div className="flex gap-3 justify-end mt-2">
            <Button variant="outline" onClick={() => setPurgeAllConfirm(false)} data-testid="cancel-purge-all-btn">Cancel</Button>
            <Button variant="destructive" onClick={handlePurgeAll} data-testid="confirm-purge-all-btn">
              <Trash2 className="h-4 w-4 mr-2" /> Permanently Delete All
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Single Purge Confirmation Dialog */}
      <Dialog open={!!purgeConfirm} onOpenChange={() => setPurgeConfirm(null)}>
        <DialogContent data-testid="purge-single-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Permanently Delete
            </DialogTitle>
          </DialogHeader>
          <p className="text-muted-foreground">
            Are you sure you want to permanently delete <strong>{purgeConfirm?.name}</strong>?
          </p>
          <p className="text-sm text-destructive font-medium">This action cannot be undone.</p>
          <div className="flex gap-3 justify-end mt-2">
            <Button variant="outline" onClick={() => setPurgeConfirm(null)} data-testid="cancel-purge-btn">Cancel</Button>
            <Button variant="destructive" onClick={() => handlePurge(purgeConfirm?.type, purgeConfirm?.id)} data-testid="confirm-purge-btn">
              <Trash2 className="h-4 w-4 mr-2" /> Permanently Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Retention Settings Dialog */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="max-w-sm" data-testid="trash-settings-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <Settings className="h-5 w-5 text-primary" /> Auto-Purge Settings
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label>Retention Period (days)</Label>
              <Input
                type="number" min="1" max="365"
                value={retentionInput}
                onChange={(e) => setRetentionInput(e.target.value)}
                data-testid="retention-days-input"
              />
              <p className="text-xs text-muted-foreground">
                Trashed items will be permanently deleted after this many days. (1–365)
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {[7, 14, 30, 60, 90].map((d) => (
                <Button
                  key={d} variant={retentionInput === String(d) ? "default" : "outline"}
                  size="sm" onClick={() => setRetentionInput(String(d))}
                  data-testid={`retention-preset-${d}`}
                >
                  {d} days
                </Button>
              ))}
            </div>
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setSettingsOpen(false)}>Cancel</Button>
              <Button onClick={handleSaveRetention} disabled={savingSettings} data-testid="save-retention-btn">
                {savingSettings ? "Saving..." : "Save"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
